import { IFormFieldValidationError } from "../types";

type SetErrorFn = (
  name: string,
  error: { type: string; message?: string }
) => void;

type ApiErrorShape = {
  response?: {
    status?: number;
    data?: {
      // The wingspan-api V2 error envelope carries per-field failures as
      // `fieldValidationErrors`; the dev mock mirrors the engine's internal
      // `validationErrors` name. Accept both.
      fieldValidationErrors?: IFormFieldValidationError[];
      validationErrors?: IFormFieldValidationError[];
      error?: string;
      message?: string;
      // The facade preserves the engine's error code (e.g. "StaleVersion",
      // "PrerequisiteNotMet") — both surface as HTTP 409, so the code is the
      // only way to tell them apart.
      errorType?: string;
    };
  };
  message?: string;
};

/**
 * Converts an engine error fieldKey to an RHF error path. Group (repeating
 * row) errors arrive as `group[i].itemKey`; RHF stores nested errors by dotted
 * path, so the index segment becomes `group.i.itemKey`. Plain keys pass through.
 */
export const serverFieldKeyToRhfPath = (fieldKey: string): string =>
  fieldKey.replace(/\[(\d+)\]/g, ".$1");

export const serverErrorsToFieldErrors = (
  setError: SetErrorFn,
  errors: IFormFieldValidationError[]
): void => {
  errors.forEach(err => {
    setError(serverFieldKeyToRhfPath(err.fieldKey as string), {
      type: err.ruleKind ?? "server",
      message: err.message
    });
  });
};

export const extractServerValidationErrors = (
  error: unknown
): IFormFieldValidationError[] => {
  const apiErr = error as ApiErrorShape;
  if (apiErr?.response?.status === 422 || apiErr?.response?.status === 400) {
    return (
      apiErr?.response?.data?.fieldValidationErrors ??
      apiErr?.response?.data?.validationErrors ??
      []
    );
  }
  return [];
};

export const extractApiErrorInfo = (
  error: unknown
): { status?: number; message?: string; errorType?: string } => {
  const apiErr = error as ApiErrorShape;
  return {
    status: apiErr?.response?.status,
    errorType: apiErr?.response?.data?.errorType,
    message:
      apiErr?.response?.data?.error ??
      apiErr?.response?.data?.message ??
      apiErr?.message
  };
};
